import { h, render, VNode, Component } from "vue";
import { removeAllEventListeners, ColorPattern } from "@zyzgroup/core-web";
import { getElementAttrs } from "./vnode";
import { Status } from "./context/context";

import Pop from "./components/pop/Pop.vue";
import PopTel from "./components/pop/PopTel.vue";
import PopCalendar from "./components/pop/PopCalendar.vue";
import RateXgPop from "./components/rate-xg/RateXgPop.vue";
import {
  type StoredRate,
  type RateShowMode
} from "./components/rate-xg/common";

import DialogToast from "./components/dialog/DialogToast.vue";
import DialogLoading from "./components/dialog/DialogLoading.vue";
import Dialog, { DialogModeType } from "./components/dialog/Dialog.vue";

import Input from "./components/form/Input.vue";

import PlateKeyboard from "./components/keyboard/PlateKeyboard.vue";
import NumberKeyboard from "./components/keyboard/NumberKeyboard.vue";

function isInCE() {
  return import.meta.env.MODE === "ce";
}

// https://cn.vitejs.dev/guide/env-and-mode
const CEMAPS: Record<string, Component> = { "yz-pop": Pop, "yz-input": Input };
export function getCEComponent(name: string) {
  if (isInCE()) {
    return name;
  } else {
    return CEMAPS[name];
  }
}

export const teleport =
  document.getElementById("teleport") || document.documentElement;

function temp() {
  const div = document.createElement("div");
  teleport.appendChild(div);
  return {
    el: div,
    remove: () => {
      try {
        teleport.removeChild(div);
      } catch (e) {
        // div 的子元素先一步被删除时，可能报错？
        console.log(e);
      }
    }
  };
}

let uniqueKey = 0;

type PopOptions = {
  fixed?: number;
  left?: string;
  top?: string;
  contentBgColor?: string;
  dur?: number;
  global?: boolean;
  closeWhenClickOverlay?: boolean;
};
// https://cn.vuejs.org/api/render-function.html
// https://cn.vuejs.org/guide/extras/render-function.html
// https://vuejs.org/guide/essentials/template-refs.html
export function pop(
  el: HTMLElement | VNode,
  directionFrom:
    | "center"
    | "bottom"
    | "top"
    | "left"
    | "right"
    | { left: string; top: string } = "bottom",
  options?: PopOptions
) {
  const t = temp();
  let vnode: VNode;
  if (el instanceof HTMLElement) {
    // 丧失 reactive
    vnode = h(
      Pop,
      {
        key: ++uniqueKey, // 使子元素重新创建，从而使浏览器计算子元素 inset 正确
        directionFrom,
        ...options,
        onClose: () => {
          t.remove();
        }
      },
      () =>
        h("div", {
          ...getElementAttrs(el),
          innerHTML: el.innerHTML // () => stringToVNodes(el.innerHTML)
        })
    );
  } else {
    // 保留 reactive
    vnode = h(
      Pop,
      {
        key: ++uniqueKey, // 使子元素重新创建，从而使浏览器计算子元素 inset 正确
        directionFrom,
        ...options,
        onClose: () => {
          t.remove();
        }
      },
      () => el
    );
  }
  render(vnode, t.el);
  vnode.component?.exposed?.open();
  return () => {
    vnode.component?.exposed?.close();
  };
}
export function popBottom(el: HTMLElement | VNode, options?: PopOptions) {
  return pop(el, "bottom", options);
}
export function popTop(el: HTMLElement | VNode, options?: PopOptions) {
  return pop(el, "top", options);
}
export function popLeft(el: HTMLElement | VNode, options?: PopOptions) {
  return pop(el, "left", options);
}
export function popRight(el: HTMLElement | VNode, options?: PopOptions) {
  return pop(el, "right", options);
}
export function popCenter(el: HTMLElement | VNode, options?: PopOptions) {
  return pop(el, "center", options);
}
export function popAbsolute(el: HTMLElement | VNode, options?: PopOptions) {
  return pop(
    el,
    { left: options.left || "0%", top: options.top || "0%" },
    options
  );
}

export function popTel(tel: string) {
  if (isInCE()) {
    // const ce = document.querySelector("yz-pop-tel#globalPopTel") as any;
    // removeAllEventListeners(ce);
    const ce = document.createElement("yz-pop-tel") as any;
    // 传入prop
    ce.tel = tel;
    // 监听事件
    ce.addEventListener("close", () => {
      teleport.removeChild(ce);
    });
    teleport.appendChild(ce);
    if (ce && "_instance" in ce) {
      (ce._instance as any).exposed.open();
    }
    return () => {
      if (ce && "_instance" in ce) {
        (ce._instance as any).exposed.close();
      }
    };
  } else {
    const t = temp();
    const vnode: VNode = h(PopTel, {
      tel,
      onClose: () => {
        t.remove();
      }
    });
    render(vnode, t.el);
    vnode.component?.exposed?.open();
    return () => {
      vnode.component?.exposed?.close();
    };
  }
}

export function popCalendar(options?: {
  title?: string;
  initDay?: string;
  startDay?: string;
  endDay?: string;
  excludeDays?: string[];
  selectedDays?: string[];
  onCancel?: () => void;
  onConfirm?: (date: Date) => void;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  if (isInCE()) {
    // const ce = document.querySelector("yz-pop-calendar#globalPopCalendar") as any;
    // removeAllEventListeners(ce);
    const ce = document.createElement("yz-pop-calendar") as any;
    // 传入prop
    ce.title = options?.title || "";
    ce.initDay = options?.initDay || "";
    ce.startDay = options?.startDay || "";
    ce.endDay = options?.endDay || "";
    ce.excludeDays = options?.excludeDays || [];
    ce.selectedDays = options?.selectedDays || [];
    // 监听事件
    ce.addEventListener("open", () => {
      options?.onOpen?.();
    });
    ce.addEventListener("close", () => {
      teleport.removeChild(ce);
      options?.onClose?.();
    });
    ce.addEventListener("cancel", () => {
      options?.onCancel?.();
    });
    ce.addEventListener("confirm", (e) => {
      options?.onConfirm?.(e.detail[0]);
    });
    teleport.appendChild(ce);
    if (ce && "_instance" in ce) {
      (ce._instance as any).exposed.open();
    }
    return () => {
      if (ce && "_instance" in ce) {
        (ce._instance as any).exposed.close();
      }
    };
  } else {
    const t = temp();
    const vnode: VNode = h(PopCalendar, {
      ...options,
      onClose: () => {
        t.remove();
        options?.onClose?.();
      }
    });
    render(vnode, t.el);
    vnode.component?.exposed?.open();
    return () => {
      vnode.component?.exposed?.close();
    };
  }
}

export function popRateXg(options?: {
  title?: string;
  mode?: RateShowMode;
  editable?: boolean;
  init?: StoredRate;
  onCancel?: () => void;
  onConfirm?: (v: StoredRate) => void;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  if (isInCE()) {
    // const ce = document.querySelector("yz-pop-rate-xg#globalPopRateXg") as any;
    // removeAllEventListeners(ce);
    const ce = document.createElement("yz-pop-rate-xg") as any;
    // 传入prop
    ce.title = options?.title || "";
    ce.mode = options?.mode || undefined;
    ce.editable = options?.editable || undefined;
    ce.init = options?.init || undefined;
    // 监听事件
    ce.addEventListener("open", () => {
      options?.onOpen?.();
    });
    ce.addEventListener("close", () => {
      teleport.removeChild(ce);
      options?.onClose?.();
    });
    ce.addEventListener("cancel", () => {
      options?.onCancel?.();
    });
    ce.addEventListener("confirm", (e) => {
      // options?.onConfirm?.(e.detail[0], e.detail[1], e.detail[2]);
      options?.onConfirm?.(e.detail[0]);
    });
    teleport.appendChild(ce);
    if (ce && "_instance" in ce) {
      (ce._instance as any).exposed.open();
    }
    return () => {
      if (ce && "_instance" in ce) {
        (ce._instance as any).exposed.close();
      }
    };
  } else {
    const t = temp();
    const vnode: VNode = h(RateXgPop, {
      ...options,
      onClose: () => {
        t.remove();
        options?.onClose?.();
      }
    });
    render(vnode, t.el);
    vnode.component?.exposed?.open();
    return () => {
      vnode.component?.exposed?.close();
    };
  }
}

export function dialog(options?: {
  title?: string;
  content?: string;
  showClose?: boolean;
  cancelText?: string;
  confirmText?: string;
  colorPattern?: ColorPattern;
  mode?: DialogModeType;
  onCancel?: () => void;
  onConfirm?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  if (isInCE()) {
    // const ce = document.querySelector("yz-dialog#globalDialog") as any;
    // removeAllEventListeners(ce);
    const ce = document.createElement("yz-dialog") as any;
    // 传入prop
    ce.title = options?.title;
    ce.content = options?.content;
    ce.showClose = options?.showClose;
    ce.cancelText = options?.cancelText;
    ce.confirmText = options?.confirmText;
    ce.colorPattern = options?.colorPattern;
    ce.mode = options?.mode;
    // 监听事件
    ce.addEventListener("open", () => {
      options?.onOpen?.();
    });
    ce.addEventListener("close", () => {
      teleport.removeChild(ce);
      options?.onClose?.();
    });
    ce.addEventListener("cancel", () => {
      options?.onCancel?.();
    });
    ce.addEventListener("confirm", () => {
      options?.onConfirm?.();
    });
    teleport.appendChild(ce);
    if (ce && "_instance" in ce) {
      (ce._instance as any).exposed.open();
    }
    return () => {
      if (ce && "_instance" in ce) {
        (ce._instance as any).exposed.close();
      }
    };
  } else {
    const t = temp();
    const vnode = h(Dialog, {
      ...options,
      onClose: () => {
        t.remove();
        options?.onClose?.();
      }
    });
    render(vnode, t.el);
    vnode.component?.exposed?.open();
    return () => {
      vnode.component?.exposed?.close();
    };
  }
}

export function toast(options?: {
  content?: string;
  during?: number;
  status?: Status;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  if (isInCE()) {
    // const ce = document.querySelector(
    //   "yz-dialog-toast#globalDialogToast"
    // ) as any;
    // removeAllEventListeners(ce);
    const ce = document.createElement("yz-dialog-toast") as any;
    ce.content = options?.content;
    ce.during = options?.during;
    ce.status = options?.status;
    ce.addEventListener("open", () => {
      options?.onOpen?.();
    });
    ce.addEventListener("close", () => {
      teleport.removeChild(ce);
      options?.onClose?.();
    });
    teleport.appendChild(ce);
    if (ce && "_instance" in ce) {
      (ce._instance as any).exposed.open();
    }
    return () => {
      if (ce && "_instance" in ce) {
        (ce._instance as any).exposed.close();
      }
    };
  } else {
    const t = temp();
    const vnode = h(DialogToast, {
      ...options,
      onClose: () => {
        t.remove();
        options?.onClose?.();
      }
    });
    render(vnode, t.el);
    vnode.component?.exposed?.open();
    return () => {
      vnode.component?.exposed?.close();
    };
  }
}

export function loading(options?: {
  content?: string;
  onOpen?: () => void;
  onClose?: () => void;
}) {
  if (isInCE()) {
    // const ce = document.querySelector(
    //   "yz-dialog-loading#globalDialogLoading"
    // ) as any;
    // removeAllEventListeners(ce);
    const ce = document.createElement("yz-dialog-loading") as any;
    ce.content = options?.content;
    ce.addEventListener("open", () => {
      options?.onOpen?.();
    });
    ce.addEventListener("close", () => {
      teleport.removeChild(ce);
      options?.onClose?.();
    });
    teleport.appendChild(ce);
    if (ce && "_instance" in ce) {
      (ce._instance as any).exposed.open();
    }
    return () => {
      if (ce && "_instance" in ce) {
        (ce._instance as any).exposed.close();
      }
    };
  } else {
    const t = temp();
    const vnode = h(DialogLoading, {
      ...options,
      onClose: () => {
        t.remove();
        options?.onClose?.();
      }
    });
    render(vnode, t.el);
    vnode.component?.exposed?.open();
    return () => {
      vnode.component?.exposed?.close();
    };
  }
}

export function plateKeyboard(options?: {
  initPlates?: string[];
  completePlateLength?: number;
  onPlates?: (plates: string[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onResize?: (rect: DOMRect) => void;
}) {
  if (isInCE()) {
    // const ce = document.querySelector(
    //   "yz-plate-keyboard#globalPlateKeyboard"
    // ) as any;
    // removeAllEventListeners(ce);
    const ce = document.createElement("yz-plate-keyboard") as any;
    ce.initPlates = options?.initPlates;
    ce.completePlateLength = options?.completePlateLength;
    ce.addEventListener("plates", (e) => {
      options?.onPlates?.(e.detail[0]);
    });
    ce.addEventListener("open", () => {
      options?.onOpen?.();
    });
    ce.addEventListener("close", () => {
      teleport.removeChild(ce);
      options?.onClose?.();
    });
    ce.addEventListener("resize", (e) => {
      options?.onResize?.(e.detail[0]);
    });
    teleport.appendChild(ce);
    if (ce && "_instance" in ce) {
      (ce._instance as any).exposed.open();
    }
    return () => {
      if (ce && "_instance" in ce) {
        (ce._instance as any).exposed.close();
      }
    };
  } else {
    const t = temp();
    const vnode = h(PlateKeyboard, {
      ...options,
      onClose: () => {
        t.remove();
        options?.onClose?.();
      }
    });
    render(vnode, t.el);
    vnode.component?.exposed?.open();
    return () => {
      vnode.component?.exposed?.close();
    };
  }
}

export function numberKeyboard(options?: {
  min?: number;
  max?: number;
  title?: string;
  onOpen?: () => void;
  onClose?: () => void;
  onNumber?: (n: number) => void;
  onError?: (msg: string) => void;
}) {
  if (isInCE()) {
    // const ce = document.querySelector(
    //   "yz-number-keyboard#globalNumberKeyboard"
    // ) as any;
    // removeAllEventListeners(ce);
    const ce = document.createElement("yz-number-keyboard") as any;
    ce.min = options?.min;
    ce.max = options?.max;
    ce.title = options?.title;
    ce.addEventListener("number", (e) => {
      options?.onNumber?.(e.detail[0]);
    });
    ce.addEventListener("error", (e) => {
      options?.onError?.(e.detail[0]);
    });
    ce.addEventListener("open", () => {
      options?.onOpen?.();
    });
    ce.addEventListener("close", () => {
      teleport.removeChild(ce);
      options?.onClose?.();
    });
    teleport.appendChild(ce);
    if (ce && "_instance" in ce) {
      (ce._instance as any).exposed.open();
    }
    return () => {
      if (ce && "_instance" in ce) {
        (ce._instance as any).exposed.close();
      }
    };
  } else {
    const t = temp();
    const vnode = h(NumberKeyboard, {
      ...options,
      onClose: () => {
        t.remove();
        options?.onClose?.();
      }
    });
    render(vnode, t.el);
    vnode.component?.exposed?.open();
    return () => {
      vnode.component?.exposed?.close();
    };
  }
}
